import { ICON } from '@/constants/icons';
import type {
  Intrusion,
  IntrusionBannerStat,
  IntrusionPanelSummary,
  IntrusionPartitionListItem,
  IntrusionPartitionRow,
  IntrusionPartitionStatusFlag,
  IntrusionTone,
  IntrusionZoneRow,
  IntrusionZoneSiblingSummary,
  IntrusionZoneStatusFlag
} from '@/views/intrusion/types';
import {
  INTRUSION_BANNER_STAT_IDS,
  INTRUSION_BANNER_STAT_LABELS,
  INTRUSION_OUT_OF_SCOPE,
  INTRUSION_PARTITION_STATE_MATCHERS,
  INTRUSION_PARTITION_STATUSES,
  INTRUSION_PARTITION_TRANSITION_MATCHERS,
  INTRUSION_STATE_GROUP,
  INTRUSION_ZONE_STATE_MATCHERS,
  INTRUSION_ZONE_STATUSES
} from '@/views/intrusion/constants';
import type { ZonePartitionArmState } from '@/views/intrusion/utilities/status-variant';
import {
  getIntrusionBannerValue,
  getIntrusionStatus
} from '@/views/intrusion/utilities/intrusion-status';
import {
  mapIntrusionEntityDetails,
  mapIntrusionEntityPlace
} from '@/views/intrusion/utilities/map-entity-details';
import {
  buildIntrusionZoneRowId,
  countUniqueIntrusionZones
} from '@/models/intrusion/helpers/zone-identity';
import {
  formatIntrusionStatusMatcherText,
  formatIntrusionStatusValues,
  hasIntrusionAlarm,
  normalizeIntrusionStatusValueKey,
  type IntrusionStatusValues
} from '@/models/intrusion/helpers/intrusion-status-values';
import type { AppColor } from '@/types/colors';

import type {
  IntrusionDetailSource,
  IntrusionDeviceQuerySource,
  IntrusionPartitionDetailSource,
  IntrusionPartitionSearchSource,
  IntrusionPartitionSourceSibling,
  IntrusionSource,
  IntrusionSourceAssignment,
  IntrusionSourcePartition,
  IntrusionSearchSource,
  IntrusionSourceZone,
  NormalizedPartitionAssignment,
  NormalizedPartitionAssignments
} from './types';

export type {
  IntrusionDetailSource,
  IntrusionDeviceQuerySource,
  IntrusionPartitionDetailSource,
  IntrusionPartitionSearchSource,
  IntrusionSearchSource
} from './types';

const BANNER_TONE_BY_COLOR: Partial<Record<AppColor, IntrusionTone>> = {
  success: 'success',
  danger: 'danger',
  warn: 'warning'
};

const ARMED_PARTITION_STATUS_PRIORITY: Array<{
  status: IntrusionPartitionStatusFlag;
  patterns: readonly string[];
}> = [
  {
    status: INTRUSION_PARTITION_STATUSES.ARMED_AWAY,
    patterns: INTRUSION_PARTITION_STATE_MATCHERS.ARMED_AWAY
  },
  {
    status: INTRUSION_PARTITION_STATUSES.ARMED_NIGHT,
    patterns: INTRUSION_PARTITION_STATE_MATCHERS.ARMED_NIGHT
  },
  {
    status: INTRUSION_PARTITION_STATUSES.ARMED_STAY,
    patterns: INTRUSION_PARTITION_STATE_MATCHERS.ARMED_STAY
  }
];

const PARTITION_DELAY_STATUS_PRIORITY: Array<{
  status: IntrusionPartitionStatusFlag;
  patterns: readonly string[];
}> = [
  {
    status: INTRUSION_PARTITION_STATUSES.ENTRY_DELAY,
    patterns: INTRUSION_PARTITION_TRANSITION_MATCHERS.ENTRY_DELAY
  },
  {
    status: INTRUSION_PARTITION_STATUSES.EXIT_DELAY,
    patterns: INTRUSION_PARTITION_TRANSITION_MATCHERS.EXIT_DELAY
  }
];

const getZoneOccupancyStatus = (stateValues: IntrusionStatusValues): IntrusionZoneStatusFlag =>
  includesAny(stateValues, INTRUSION_ZONE_STATE_MATCHERS.OPEN)
    ? INTRUSION_ZONE_STATUSES.OPEN
    : INTRUSION_ZONE_STATUSES.CLOSED;

const naturalCompare = (left: string | number | null | undefined, right: string | number | null | undefined): number =>
  String(left ?? '').localeCompare(String(right ?? ''), undefined, { numeric: true, sensitivity: 'base' });

const includesAny = (value: IntrusionStatusValues, patterns: readonly string[]): boolean => {
  const normalized = normalizeIntrusionStatusValueKey(formatIntrusionStatusMatcherText(value));

  return patterns.some((pattern) => normalized.includes(normalizeIntrusionStatusValueKey(pattern)));
};

const getBannerTone = (color: AppColor): IntrusionTone => BANNER_TONE_BY_COLOR[color] ?? 'neutral';

const mapPlace = (device: IntrusionSource): Intrusion['place'] => ({
  id: device?.gateway?.place?.id || undefined,
  name: device?.gateway?.place?.name || 'Unassigned'
});

const mapGateway = (device: IntrusionSource): Intrusion['gateway'] => ({
  id: device?.gateway?.id || undefined,
  name: device?.gateway?.name || 'Unknown Gateway',
  connectionStatus: device?.gateway?.connectionStatus ?? undefined,
  syncStatus: device?.gateway?.syncStatus ?? undefined,
  type: device?.gateway?.type ?? undefined
});

const getIpAddress = (device: IntrusionSource): string => device?.deviceAddress || 'Unknown';

const getMacAddress = (device: IntrusionSource): string => device?.deviceMac || 'Unknown';

const hasStatusFlag = <T extends string>(flags: readonly T[], target: T): boolean => flags.includes(target);

/** Shared with `map-zone.ts` so a standalone zone's status flags match the embedded-row logic exactly. */
export const getZoneStatusFlags = (
  zone: Pick<IntrusionSourceZone, 'stateValues' | 'alarmValues'>
): IntrusionZoneStatusFlag[] => {
  const stateValues = zone.stateValues;
  const alarmValues = zone.alarmValues;

  return [
    // `alarmValues` names the alarms ("Burglary", "Fire", ...) rather than saying "Alarm", so any
    // entry means in-alarm; the `stateValues` match stays for panels that report it as a state.
    ...(hasIntrusionAlarm(alarmValues) || includesAny(stateValues, INTRUSION_ZONE_STATE_MATCHERS.ALARM)
      ? [INTRUSION_ZONE_STATUSES.ALARM]
      : []),
    ...(includesAny(stateValues, INTRUSION_ZONE_STATE_MATCHERS.TROUBLE)
      ? [INTRUSION_ZONE_STATUSES.TROUBLE]
      : []),
    getZoneOccupancyStatus(stateValues),
    ...(includesAny(stateValues, INTRUSION_ZONE_STATE_MATCHERS.BYPASSED)
      ? [INTRUSION_ZONE_STATUSES.BYPASSED]
      : [])
  ];
};

export const getPartitionStatusFlags = (
  partition: Pick<IntrusionSourcePartition, 'stateValues' | 'alarmValues'>
): IntrusionPartitionStatusFlag[] => {
  const values = partition.stateValues;
  // Alarm is orthogonal to arm state - it comes from the `alarm` bitmask, not `state` - so it leads
  // the flags rather than replacing them: an armed partition in alarm is [Alarm, Armed Away]. It's
  // first so `getStatusCellVariant`'s priority resolves the row to the alarm colour.
  const alarmFlags = hasIntrusionAlarm(partition.alarmValues) ? [INTRUSION_PARTITION_STATUSES.ALARM] : [];
  const delayFlags = PARTITION_DELAY_STATUS_PRIORITY
    .filter((entry) => includesAny(values, entry.patterns))
    .map((entry) => entry.status);
  const armedStatus = ARMED_PARTITION_STATUS_PRIORITY
    .find((entry) => includesAny(values, entry.patterns))
    ?.status;

  if (armedStatus) {
    return [...alarmFlags, ...delayFlags, armedStatus];
  }

  const readinessStatus = includesAny(values, INTRUSION_PARTITION_STATE_MATCHERS.NOT_READY)
    ? INTRUSION_PARTITION_STATUSES.NOT_READY
    : includesAny(values, INTRUSION_PARTITION_STATE_MATCHERS.READY)
      ? INTRUSION_PARTITION_STATUSES.READY
      : INTRUSION_PARTITION_STATUSES.NOT_READY;

  return [
    ...alarmFlags,
    ...delayFlags,
    INTRUSION_PARTITION_STATUSES.DISARMED,
    readinessStatus
  ];
};

const PARTITION_ARMED_FLAGS: readonly IntrusionPartitionStatusFlag[] = [
  INTRUSION_PARTITION_STATUSES.ARMED_AWAY,
  INTRUSION_PARTITION_STATUSES.ARMED_STAY,
  INTRUSION_PARTITION_STATUSES.ARMED_NIGHT
];

const PARTITION_DELAY_FLAGS: readonly IntrusionPartitionStatusFlag[] = [
  INTRUSION_PARTITION_STATUSES.EXIT_DELAY,
  INTRUSION_PARTITION_STATUSES.ENTRY_DELAY
];

export const getPartitionArmState = (
  statusFlags: readonly IntrusionPartitionStatusFlag[]
): ZonePartitionArmState => {
  if (PARTITION_DELAY_FLAGS.some((flag) => statusFlags.includes(flag))) return 'transitional';
  if (PARTITION_ARMED_FLAGS.some((flag) => statusFlags.includes(flag))) return 'armed';

  return 'disarmed';
};

export const combineZonePartitionArmStates = (
  armStates: readonly ZonePartitionArmState[]
): ZonePartitionArmState => {
  if (!armStates.length) return 'unknown';
  if (armStates.includes('transitional')) return 'transitional';
  if (armStates.includes('armed')) return 'armed';

  return armStates.every((state) => state === 'unknown') ? 'unknown' : 'disarmed';
};

const buildPartitionId = (deviceId: string, partition: IntrusionSourcePartition): string =>
  partition?.id || `${deviceId}-partition-${partition?.index ?? partition?.name ?? 'unknown'}`;

const buildZoneId = (deviceId: string, assignment: IntrusionSourceAssignment, zone: IntrusionSourceZone): string =>
  assignment?.zoneID || zone?.id || `${deviceId}-zone-${zone?.index ?? zone?.name ?? 'unknown'}`;

const getEnabledAssignments = (partition: IntrusionSourcePartition): NormalizedPartitionAssignment[] =>
  (partition?.PartitionZoneAssignments || []).filter(
    (assignment): assignment is NormalizedPartitionAssignment =>
      assignment?.enabled !== false && Boolean(assignment?.IntrusionZone)
  );

const getEnabledPartitions = (device: IntrusionDetailSource): IntrusionSourcePartition[] =>
  (device?.IntrusionPartitions || [])
    .filter((partition) => partition?.enabled !== false)
    .sort((left, right) => naturalCompare(left?.index, right?.index));

const normalizePartitionAssignments = (device: IntrusionDetailSource): NormalizedPartitionAssignments[] => {
  const partitions = getEnabledPartitions(device);

  return partitions.map((partition) => {
    const partitionKey = buildPartitionId(device?.id, partition);

    return {
      partition,
      partitionKey,
      assignments: getEnabledAssignments(partition).sort((left, right) =>
        naturalCompare(left?.IntrusionZone?.index, right?.IntrusionZone?.index)
      )
    };
  });
};

const mapZone = (
  deviceId: string,
  partitionKey: string,
  assignment: IntrusionSourceAssignment,
  zone: IntrusionSourceZone,
  partitionArmState: ZonePartitionArmState
): IntrusionZoneRow => {
  const statusFlags = getZoneStatusFlags(zone);
  const zoneId = buildZoneId(deviceId, assignment, zone);
  // Prefer the resolved `VideoSource` relation over the `defaultVideoSourceID` scalar - both
  // represent the same assignment, but the scalar hasn't proven reliable this deep in the query
  // (Device -> IntrusionPartitions -> PartitionZoneAssignments -> IntrusionZone).
  const videoSourceID = zone?.VideoSource?.id || zone?.defaultVideoSourceID || undefined;

  return {
    id: buildIntrusionZoneRowId(partitionKey, zoneId),
    zoneId,
    name: (zone.name ?? '').trim(),
    index: zone.index,
    type: 'Zone',
    zoneType: zone.type || 'Zone',
    status: formatIntrusionStatusValues(zone.stateValues, zone.alarmValues),
    statusFlags,
    partitionArmState,
    hasCamera: Boolean(videoSourceID),
    videoSourceID,
    canBypass: zone.canBypass
  };
};

const mapPartition = (
  deviceId: string,
  partitionKey: string,
  partition: IntrusionSourcePartition,
  assignments: NormalizedPartitionAssignment[]
): IntrusionPartitionRow => {
  const statusFlags = getPartitionStatusFlags(partition);
  const partitionArmState = getPartitionArmState(statusFlags);
  const children = assignments.map((assignment) =>
    mapZone(deviceId, partitionKey, assignment, assignment.IntrusionZone, partitionArmState)
  );

  return {
    id: buildPartitionId(deviceId, partition),
    partitionId: partition.id || undefined,
    name: (partition.name ?? '').trim(),
    index: null,
    type: 'Partition',
    status: formatIntrusionStatusValues(partition.stateValues, partition.alarmValues),
    statusFlags,
    canArm: partition.canArm,
    canDisarm: partition.canDisarm,
    children
  };
};

const buildStateGroups = (device: IntrusionDetailSource): Intrusion['stateGroups'] => {
  const partitions = normalizePartitionAssignments(device).map(({ partition, partitionKey, assignments }) =>
    mapPartition(device?.id, partitionKey, partition, assignments)
  );

  if (!partitions.length) {
    return [];
  }

  const zoneCount = countUniqueIntrusionZones(partitions);

  return [
    {
      id: `${device?.id}-${INTRUSION_STATE_GROUP.ID_SUFFIX}`,
      title: INTRUSION_STATE_GROUP.TITLE,
      subtitle: `${partitions.length} partitions, ${zoneCount} zones`,
      items: partitions
    }
  ];
};

const isArmedPartitionStatus = (statusFlags: IntrusionPartitionRow['statusFlags']): boolean =>
  hasStatusFlag(statusFlags, INTRUSION_PARTITION_STATUSES.ARMED_AWAY) ||
  hasStatusFlag(statusFlags, INTRUSION_PARTITION_STATUSES.ARMED_STAY) ||
  hasStatusFlag(statusFlags, INTRUSION_PARTITION_STATUSES.ARMED_NIGHT);

const buildBannerStats = (
  device: IntrusionSource,
  partitionCount: number,
  zoneCount: number,
  armedPartitionCount: number
): IntrusionBannerStat[] => {
  const status = getIntrusionStatus(device);

  return [
    {
      id: INTRUSION_BANNER_STAT_IDS.STATUS,
      label: INTRUSION_BANNER_STAT_LABELS.STATUS,
      value: getIntrusionBannerValue(),
      icon: ICON.INTRUSION_PANEL,
      tone: getBannerTone(status.color)
    },
    {
      id: INTRUSION_BANNER_STAT_IDS.PARTITION_COUNT,
      label: INTRUSION_BANNER_STAT_LABELS.PARTITIONS,
      value: String(partitionCount),
      icon: ICON.LAYER_GROUP,
      tone: armedPartitionCount > 0 ? 'warning' : 'neutral'
    },
    {
      id: INTRUSION_BANNER_STAT_IDS.ZONE_COUNT,
      label: INTRUSION_BANNER_STAT_LABELS.ZONES,
      value: String(zoneCount),
      icon: ICON.SENSOR,
      tone: 'neutral'
    }
  ];
};

const getSourceCounts = (device: IntrusionSource) => ({
  partitions: 'intrusionPartitionCount' in device ? device.intrusionPartitionCount?.length ?? 0 : 0,
  zones: 'intrusionZoneCount' in device ? device.intrusionZoneCount?.length ?? 0 : 0
});

const getIntegrationKey = (device: IntrusionSource): string =>
  'dscConnections' in device ? device.dscConnections?.[0]?.integrationKey || '' : '';

const buildBaseIntrusion = (device: IntrusionSource) => {
  const { partitions, zones } = getSourceCounts(device);

  return {
    id: device?.id,
    enabled: device?.enabled ?? true,
    name: device?.name || 'Unnamed Intrusion',
    description: device?.description || '',
    place: mapPlace(device),
    gateway: mapGateway(device),
    ipAddress: getIpAddress(device),
    connectionStatus: device?.connectionStatus ?? null,
    make: device?.make || 'Unknown',
    model: device?.model || 'Unknown',
    macAddress: getMacAddress(device),
    serialNumber: device?.serial || '',
    firmware: device?.firmware || '',
    integrationKey: getIntegrationKey(device),
    partitions,
    zones,
    stateGroups: []
  } satisfies Omit<Intrusion, 'bannerStats'>;
};

const getPartitionRows = (stateGroups: Intrusion['stateGroups']) =>
  stateGroups.flatMap((group) => group.items);

const countPartitionsInGroups = (stateGroups: Intrusion['stateGroups']): number =>
  getPartitionRows(stateGroups).length;

const countZonesInGroups = (stateGroups: Intrusion['stateGroups']): number =>
  countUniqueIntrusionZones(getPartitionRows(stateGroups));

const countArmedPartitions = (stateGroups: Intrusion['stateGroups']): number =>
  getPartitionRows(stateGroups).filter((partition) => isArmedPartitionStatus(partition.statusFlags)).length;

export const mapIntrusionSearchItem = (device: IntrusionDeviceQuerySource | IntrusionSearchSource): Intrusion => {
  const intrusion = buildBaseIntrusion(device);

  return {
    ...intrusion,
    bannerStats: buildBannerStats(device, intrusion.partitions, intrusion.zones, 0)
  };
};

export const mapIntrusionDetail = (device: IntrusionDetailSource): Intrusion => {
  const baseIntrusion = buildBaseIntrusion(device);
  const stateGroups = buildStateGroups(device);
  const partitions = countPartitionsInGroups(stateGroups);
  const zones = countZonesInGroups(stateGroups);
  const armedPartitionCount = countArmedPartitions(stateGroups);

  return {
    ...baseIntrusion,
    partitions,
    zones,
    stateGroups,
    bannerStats: buildBannerStats(device, partitions, zones, armedPartitionCount)
  };
};

export const mapIntrusionPartitionSearchItem = (
  source: IntrusionPartitionSearchSource
): IntrusionPartitionListItem => {
  const device = source.Device;
  const statusFlags = getPartitionStatusFlags(source);
  // `enabled` is already filtered server-side (the query's `where` clause); still exclude
  // assignments whose zone was deleted, matching `getEnabledAssignments`' filter for the detail
  // query path so the sidebar's count agrees with the dropped widget's rendered zone list.
  const partitionArmState = getPartitionArmState(statusFlags);
  const zones = (source.PartitionZoneAssignments ?? [])
    .filter((assignment) => Boolean(assignment.IntrusionZone))
    .map((assignment) => ({
      statusFlags: getZoneStatusFlags(assignment.IntrusionZone!),
      partitionArmState
    }));

  return {
    id: source.id,
    partitionId: source.id,
    name: (source.name ?? '').trim(),
    status: formatIntrusionStatusValues(source.stateValues, source.alarmValues),
    statusFlags,
    zoneCount: zones.length,
    zones,
    deviceAddress: device?.deviceAddress || undefined,
    deviceConnectionStatus: device?.connectionStatus ?? undefined,
    panel: {
      id: device?.id || undefined,
      name: device?.name || INTRUSION_OUT_OF_SCOPE,
      connectionStatus: device?.connectionStatus ?? undefined
    },
    place: mapIntrusionEntityPlace(source, device),
    gateway: {
      id: device?.gateway?.id || undefined,
      name: device?.gateway?.name || INTRUSION_OUT_OF_SCOPE,
      connectionStatus: device?.gateway?.connectionStatus ?? undefined,
      syncStatus: device?.gateway?.syncStatus ?? undefined,
      type: device?.gateway?.type ?? undefined
    }
  };
};

const mapPartitionSiblingZones = (
  assignments: IntrusionPartitionSourceSibling['PartitionZoneAssignments']
): IntrusionZoneSiblingSummary[] =>
  (assignments || [])
    .filter((assignment) => Boolean(assignment?.IntrusionZone))
    .map((assignment) => ({
      id: assignment.IntrusionZone!.id,
      zoneId: assignment.zoneID || assignment.IntrusionZone!.id,
      name: (assignment.IntrusionZone!.name ?? '').trim(),
      index: assignment.IntrusionZone!.index ?? null,
      statusFlags: getZoneStatusFlags(assignment.IntrusionZone!)
    }))
    .sort((left, right) => naturalCompare(left.index, right.index));

export const mapIntrusionPartitionDetail = (
  source: IntrusionPartitionDetailSource
): { partition: IntrusionPartitionRow; panel: IntrusionPanelSummary } => {
  const device = source.Device;
  const partitionKey = source.id;
  const assignments = getEnabledAssignments(source).sort((left, right) =>
    naturalCompare(left?.IntrusionZone?.index, right?.IntrusionZone?.index)
  );
  const partition: IntrusionPartitionRow = {
    ...mapPartition(device?.id, partitionKey, source, assignments),
    ...mapIntrusionEntityDetails(source, device)
  };

  const siblings = [...(device?.IntrusionPartitions || [])]
    .sort((left, right) => naturalCompare(left?.index, right?.index))
    .map((sibling) => ({
      id: sibling.id,
      name: (sibling.name ?? '').trim(),
      index: sibling.index ?? null,
      status: formatIntrusionStatusValues(sibling.stateValues, sibling.alarmValues),
      statusFlags: getPartitionStatusFlags(sibling),
      zones: mapPartitionSiblingZones(sibling.PartitionZoneAssignments)
    }));

  const panel: IntrusionPanelSummary = {
    id: device?.id,
    name: device?.name || INTRUSION_OUT_OF_SCOPE,
    description: device?.description || undefined,
    connectionStatus: device?.connectionStatus ?? null,
    place: {
      id: device?.gateway?.place?.id || undefined,
      name: device?.gateway?.place?.name || INTRUSION_OUT_OF_SCOPE
    },
    gateway: {
      id: device?.gateway?.id || undefined,
      name: device?.gateway?.name || INTRUSION_OUT_OF_SCOPE,
      connectionStatus: device?.gateway?.connectionStatus ?? undefined,
      syncStatus: device?.gateway?.syncStatus ?? undefined,
      type: device?.gateway?.type ?? undefined
    },
    partitions: siblings
  };

  return { partition, panel };
};
